/* ═══════════════════════════════════════════════════════════════════════════
   Entitlements / gating

   One provider (mounted at the layout level) loads the org's plan + token
   balance once, and exposes `useGate()` so any feature can guard an action:

     const gate = useGate();
     // pay-to-ship: export/publish/share require a paid plan
     gate.requirePaid(() => doExport(), "Exporting");
     // translation requires tokens (Azure Translator, billed per character)
     gate.requireTokens(text.length, () => translate());

   If the user isn't entitled, the matching prompt (Upgrade / Buy tokens) opens
   instead of running the action. Backend still enforces this for real (402/403);
   this is the UX layer. Currently mock-backed via subscription.service.
   ═══════════════════════════════════════════════════════════════════════════ */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { Modal } from "antd";
import { useNavigate } from "react-router-dom";
import { LockOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import "./Subscription.css";
import {
  getPlans,
  getSeatUsage,
  getSubscription,
  getTokenBalance,
  getCurrentPlan,
} from "./subscription.service";
import { BillingCycle, Plan, SeatUsage, TokenBalance } from "./subscription.types";
import BuyTokensModal from "./components/BuyTokensModal";
import {
  CanonicalRole,
  getCurrentRole,
  canManageBilling as canManageBillingFor,
  canCreateProject as canCreateProjectFor,
  isWorkingSeat,
} from "../../utils/roles";

interface GateApi {
  loading: boolean;
  plan: Plan | null;
  /** Current user's canonical role (owner/manager/analyst/contributor/viewer). */
  role: CanonicalRole | null;
  /** Billing & subscription management — Owner only. */
  canManageBilling: boolean;
  /** Create a project — Owner or Manager (Owner is a superset of Manager). */
  canCreateProject: boolean;
  /** Working-seat usage vs the plan cap (Owner/Manager/Analyst). */
  seatUsage: SeatUsage | null;
  /**
   * Run `action` if a working seat is free (or the invited role is a free
   * Contributor/Viewer), else open the seat-limit prompt.
   */
  requireSeat: (invitedRole: CanonicalRole | null, action: () => void) => void;
  /** On a paid (non-Free) plan → export / publish / share unlocked. */
  isPaid: boolean;
  canExport: boolean;
  canPublish: boolean;
  tokenBalance: TokenBalance | null;
  /** Does the org have enough tokens for `chars` characters? (chars<=0 → balance>0) */
  hasTokens: (chars?: number) => boolean;
  /** Run `action` if on a paid plan, else open the Upgrade prompt. */
  requirePaid: (action: () => void, feature?: string) => void;
  /** Run `action` if enough tokens, else open the Buy-tokens prompt. */
  requireTokens: (chars: number, action: () => void) => void;
  refresh: () => void;
}

const EntitlementsContext = createContext<GateApi | null>(null);

export const useGate = (): GateApi => {
  const ctx = useContext(EntitlementsContext);
  if (!ctx) throw new Error("useGate must be used inside <EntitlementsProvider>");
  return ctx;
};

export const EntitlementsProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { t } = useTranslation("subscription");
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [plan, setPlan] = useState<Plan | null>(null);
  const [role, setRole] = useState<CanonicalRole | null>(null);
  const [tokenBalance, setTokenBalance] = useState<TokenBalance | null>(null);
  const [seatUsage, setSeatUsage] = useState<SeatUsage | null>(null);
  const [billingCycle, setBillingCycle] = useState<BillingCycle>("monthly");
  const [upgradeFeature, setUpgradeFeature] = useState<string | null>(null);
  const [buyTokensOpen, setBuyTokensOpen] = useState(false);
  const [seatLimitOpen, setSeatLimitOpen] = useState(false);
  const [seatCostConfirm, setSeatCostConfirm] = useState<{
    action: () => void;
    extraPrice: number;
  } | null>(null);

  const load = useCallback(async () => {
    try {
      const [, sub, bal, seats, currentPlan] = await Promise.all([
        getPlans(),
        getSubscription(),
        getTokenBalance(),
        getSeatUsage(),
        getCurrentPlan(),
      ]);
      setPlan(currentPlan ?? null);
      setRole(getCurrentRole());
      setTokenBalance(bal);
      setSeatUsage(seats);
      setBillingCycle(sub.billingCycle);
    } catch {
      /* keep defaults; treated as not-entitled */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const isPaid = !!plan && !plan.isFree;
  const canExport = isPaid;
  const canPublish = isPaid;
  const canManageBilling = canManageBillingFor(role);
  const canCreateProject = canCreateProjectFor(role);

  const hasTokens = useCallback(
    (chars = 0) => {
      if (plan?.unlimitedCredits) return true; // unlimited plans always have tokens
      if (!tokenBalance) return false;
      if (chars <= 0) return tokenBalance.balance > 0;
      const needed = Math.ceil(chars / tokenBalance.charsPerToken);
      return tokenBalance.balance >= needed;
    },
    [tokenBalance, plan?.unlimitedCredits],
  );

  const requirePaid = useCallback(
    (action: () => void, feature?: string) => {
      if (isPaid) action();
      else setUpgradeFeature(feature ?? t("entitlements.defaultFeatureName"));
    },
    [isPaid, t],
  );

  const requireTokens = useCallback(
    (chars: number, action: () => void) => {
      if (hasTokens(chars)) action();
      else setBuyTokensOpen(true);
    },
    [hasTokens],
  );

  const requireSeat = useCallback(
    (invitedRole: CanonicalRole | null, action: () => void) => {
      // Contributor / Viewer are free and uncapped.
      if (!isWorkingSeat(invitedRole)) {
        action();
        return;
      }
      const used = seatUsage?.workingUsersUsed ?? 0;
      const limit = seatUsage?.workingUserLimit ?? null;
      if (limit != null && used >= limit) {
        setSeatLimitOpen(true);
        return;
      }
      // Tiered plans bundle a number of seats into the base price — adding
      // one beyond that (but still under the plan cap) costs extra, so warn
      // before finalizing the invite instead of billing the customer silently.
      const included = plan?.includedWorkingUsers;
      const extraPrice =
        billingCycle === "yearly" ? plan?.additionalUserPriceYearly : plan?.additionalUserPrice;
      if (included != null && used >= included && extraPrice != null) {
        setSeatCostConfirm({ action, extraPrice });
      } else {
        action();
      }
    },
    [seatUsage, plan, billingCycle],
  );

  const api: GateApi = {
    loading,
    plan,
    role,
    canManageBilling,
    canCreateProject,
    seatUsage,
    requireSeat,
    isPaid,
    canExport,
    canPublish,
    tokenBalance,
    hasTokens,
    requirePaid,
    requireTokens,
    refresh: load,
  };

  return (
    <EntitlementsContext.Provider value={api}>
      {children}

      <Modal
        open={!!upgradeFeature}
        onCancel={() => setUpgradeFeature(null)}
        footer={null}
        centered
        width={420}
        className="sub-modal"
      >
        <div
          className="sub-success-check"
          style={{ background: "#f4f1fe", color: "#7c3aed" }}
        >
          <LockOutlined />
        </div>
        <h3 className="sub-modal-title">{t("entitlements.upgradeRequired.title")}</h3>
        <p className="sub-modal-sub">
          {t("entitlements.upgradeRequired.body", { feature: upgradeFeature })}
        </p>
        <div className="sub-modal-actions">
          <button
            className="sub-btn sub-btn-outline"
            onClick={() => setUpgradeFeature(null)}
          >
            {t("common.notNow")}
          </button>
          <button
            className="sub-btn sub-btn-primary"
            onClick={() => {
              setUpgradeFeature(null);
              navigate("/subscription-plans");
            }}
          >
            {t("common.seePlans")}
          </button>
        </div>
      </Modal>

      <Modal
        open={seatLimitOpen}
        onCancel={() => setSeatLimitOpen(false)}
        footer={null}
        centered
        width={420}
        className="sub-modal"
      >
        <div
          className="sub-success-check"
          style={{ background: "#f4f1fe", color: "#7c3aed" }}
        >
          <LockOutlined />
        </div>
        <h3 className="sub-modal-title">{t("entitlements.seatLimit.title")}</h3>
        <p className="sub-modal-sub">{t("entitlements.seatLimit.body")}</p>
        <div className="sub-modal-actions">
          <button
            className="sub-btn sub-btn-outline"
            onClick={() => setSeatLimitOpen(false)}
          >
            {t("common.notNow")}
          </button>
          <button
            className="sub-btn sub-btn-primary"
            onClick={() => {
              setSeatLimitOpen(false);
              navigate("/subscription-plans");
            }}
          >
            {t("common.seePlans")}
          </button>
        </div>
      </Modal>

      <Modal
        open={!!seatCostConfirm}
        onCancel={() => setSeatCostConfirm(null)}
        footer={null}
        centered
        width={420}
        className="sub-modal"
      >
        <div
          className="sub-success-check"
          style={{ background: "#f4f1fe", color: "#7c3aed" }}
        >
          <LockOutlined />
        </div>
        <h3 className="sub-modal-title">{t("entitlements.seatCost.title")}</h3>
        <p className="sub-modal-sub">
          {t("entitlements.seatCost.bodyStart", {
            count: plan?.includedWorkingUsers ?? 0,
          })}{" "}
          <strong>${seatCostConfirm?.extraPrice}/month</strong>{" "}
          {t("entitlements.seatCost.bodyEnd")}
        </p>
        <div className="sub-modal-actions">
          <button
            className="sub-btn sub-btn-outline"
            onClick={() => setSeatCostConfirm(null)}
          >
            {t("common.cancel")}
          </button>
          <button
            className="sub-btn sub-btn-primary"
            onClick={() => {
              const pending = seatCostConfirm?.action;
              setSeatCostConfirm(null);
              pending?.();
            }}
          >
            {t("entitlements.seatCost.addButton", {
              price: seatCostConfirm?.extraPrice,
            })}
          </button>
        </div>
      </Modal>

      <BuyTokensModal
        open={buyTokensOpen}
        onClose={() => setBuyTokensOpen(false)}
        onBought={() => {
          setBuyTokensOpen(false);
          load();
        }}
      />
    </EntitlementsContext.Provider>
  );
};
